import React from 'react';
import { motion } from 'framer-motion';
import { 
  Factory, 
  Truck, 
  Receipt, 
  BarChart3, 
  CheckCircle, 
  ArrowRight,
  Scale,
  Flame,
  MessageSquare,
  PhoneCall
} from 'lucide-react';
import './ErpSoftware.css';
import FAQAccordion from './FAQAccordion';

const erpFeatures = [
  {
    icon: <Factory size={24} />,
    title: 'Custom Steel & Manufacturing ERP',
    desc: 'Specialized production tracking, scrap management, heat batch monitoring, and yield calculation engineered specifically for steel rolling mills, foundries, and industrial plants.'
  },
  {
    icon: <Scale size={24} />,
    title: 'Digital Weighbridge Automation',
    desc: 'Connect digital weighbridges directly to your ERP database. Eliminate manual weight tampering, automate tare weight deduction, and generate instant gross weight slips.'
  },
  {
    icon: <Receipt size={24} />,
    title: 'Automated E-Invoicing & E-Way Bills',
    desc: 'Direct GST portal API integration for one-click e-invoicing, e-way bill generation, tax ledger sync, and automated Tally ERP accounting reconciliation.'
  },
  {
    icon: <Truck size={24} />,
    title: 'Dispatch, Gatepass & Driver Verification',
    desc: 'Automate truck entry/exit gate passes, driver verification, loading slips, delivery challans, and real-time dispatch tracking.'
  },
  {
    icon: <Flame size={24} />,
    title: 'Heat Batch & Billet Yield Analysis',
    desc: 'Track furnace heat chemistry, billet-to-rebar rolling yield ratios, burning losses, and heat number traceability across every production batch.'
  },
  {
    icon: <BarChart3 size={24} />,
    title: 'Executive Financial & Cost Analysis',
    desc: 'Live cost-per-tonne calculation, profit-margin breakdown per batch, raw material stock forecasting, and vendor payment automation.'
  }
];

const erpProcessDetailed = [
  { num: '01', title: 'On-Site Plant Audit & Scope', desc: 'Our ERP architects visit your manufacturing plant or foundry to map raw material yards, weighbridges, furnace heat tracking, and gate pass flows.' },
  { num: '02', title: 'Weighbridge & Hardware Drivers', desc: 'Configuring RS232/Ethernet hardware drivers to stream live digital scale weight directly into the ERP database.' },
  { num: '03', title: 'Custom Module & Tally Ledger Sync', desc: 'Setting up billet yield formulas, scrap loss logs, multi-warehouse stock ledgers, and automated Tally Prime accounting vouchers.' },
  { num: '04', title: 'On-Floor Operator & Staff Training', desc: 'Conducting hands-on training for weighbridge operators, dispatch clerks, accounts managers, and plant superintendents.' },
  { num: '05', title: 'Live Plant Trial & Go-Live', desc: 'Running parallel trials with live truck dispatch, heat batch logging, and GST e-way bill generation before full plant go-live.' },
  { num: '06', title: '24/7 SLA AMC & On-Site Support', desc: 'Providing continuous SLA-backed 24/7 technical maintenance, regular database backups, and feature upgrades.' }
];

const erpModules = [
  'Furnace Heat Batch Tracking', 'Billet-to-Rebar Yield Ratio',
  'Weighbridge Automation', 'Scrap Weight & Moisture Loss Log',
  'Raw Material Multi-Store Stock', 'Finished Goods Yard Inventory',
  'GST E-Way Bill & E-Invoice Portal', 'Tally ERP Ledger Sync',
  'Gatepass & Vehicle Entry Slips', 'Cost-Per-Tonne Live Dashboard',
  'Vendor Payment Ledger', 'Quality Lab Chemical Analysis'
];

const erpFaqsDetailed = [
  { q: 'How is manufacturing ERP development and implementation done step-by-step?', a: 'Our implementation follows 6 stages: (1) On-Site Plant Audit, (2) Weighbridge & Sensor Hardware Integration, (3) Custom Module & Tally Sync, (4) Operator Training, (5) Live Plant Trial & Go-Live, and (6) 24/7 SLA Maintenance.' },
  { q: 'Can your ERP integrate directly with our digital weighbridge indicator?', a: 'Yes! We build direct RS232/Ethernet middleware to capture weight data directly from your weighbridge indicator into the ERP database, eliminating manual entry fraud.' },
  { q: 'Does the ERP sync automatically with Tally ERP / Prime?', a: 'Yes! All sales invoices, purchase vouchers, GST ledgers, and inventory journals sync automatically with Tally Prime in real time.' },
  { q: 'Is this ERP customizable for steel rolling mills in Sahibabad & Ghaziabad?', a: 'Absoltely. We specialize in custom steel ERP development tailored for Ghaziabad rolling mills, foundries, and pipe manufacturers based on your exact plant workflows.' },
  { q: 'Do we get source code ownership and local database control?', a: 'Yes, we can deploy the ERP database locally on your factory server or on a private cloud with 100% data privacy and source code ownership.' },
  { q: 'How does the ERP handle furnace heat batch tracking?', a: 'The ERP logs chemistry parameters (Carbon, Manganese, Silicon content), billet weight, rebar yield tonnage, and burning loss percentage per furnace heat.' },
  { q: 'What happens if internet fails at our factory plant?', a: 'Our plant ERP supports local offline server deployment so weighbridge weighing and gate pass generation continue uninterrupted even during internet outages.' }
];

export default function ErpSoftware() {
  const whatsappUrl = "https://wa.me/918287999236?text=Hi%20Wayug!%20I%20am%20interested%20in%20a%20demo%20of%20your%20Manufacturing%20%26%20Steel%20ERP%20Software.";

  return (
    <section className="erp-section">
      <div className="container">
        
        {/* Hero */}
        <motion.div 
          className="erp-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="erp-badge">
            <Factory size={16} /> Custom ERP Development Service
          </div>
          <h1 className="erp-title">
            Custom ERP Development for <span className="gradient-text">Clients &amp; Businesses</span>
          </h1>
          <p className="erp-subtitle">
            We don't sell generic off-the-shelf ERPs. We engineer bespoke Custom ERPs, web applications, mobile apps (Android &amp; iOS), and enterprise software built from scratch to match your exact business requirements, plant workflows, and operational needs.
          </p>
          <div style={{ marginTop: '2rem', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href={whatsappUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              Discuss Your Custom ERP Project <ArrowRight size={16} />
            </a>
            <a href="/contact" className="btn btn-outline">
              Get Free Assessment &amp; Quote
            </a>
          </div>
        </motion.div>

        {/* Feature Grid */}
        <div className="erp-features-grid">
          {erpFeatures.map((item, idx) => (
            <motion.div 
              key={item.title}
              className="erp-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
            >
              <div className="erp-card-icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* 6-Step Implementation Process */}
        <div style={{ margin: '80px 0' }}>
          <h2 className="text-center" style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '12px' }}>
            How Plant ERP Implementation Is Done: <span className="gradient-text">Our 6-Step Process</span>
          </h2>
          <p className="text-center" style={{ color: '#a3a3a3', maxWidth: '700px', margin: '0 auto 40px', fontSize: '1rem', lineHeight: '1.6' }}>
            From initial plant audit to live weighbridge automation, here is how we digitize your manufacturing floor operations.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {erpProcessDetailed.map((step) => (
              <div key={step.num} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '28px' }}>
                <span style={{ fontSize: '2rem', fontWeight: '800', color: '#10b981' }}>{step.num}</span>
                <h4 style={{ fontSize: '1.2rem', fontWeight: '700', margin: '12px 0 8px', color: '#fff' }}>{step.title}</h4>
                <p style={{ color: '#a3a3a3', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Module Checklist */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '40px', margin: '60px 0' }}>
          <h3 style={{ fontSize: '1.8rem', fontWeight: '800', textAlign: 'center', marginBottom: '24px' }}>
            Steel &amp; Manufacturing ERP Modules
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            {erpModules.map((m) => (
              <div key={m} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e5e5e5', fontSize: '0.95rem' }}>
                <CheckCircle size={16} color="#10b981" /> {m}
              </div>
            ))}
          </div>
        </div>

        {/* Special Steel Industry Highlight Box */}
        <motion.div 
          className="erp-steel-box text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <h2 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '1rem' }}>
            Specially Built for Ghaziabad &amp; NCR Steel Manufacturers
          </h2>
          <p style={{ color: '#a3a3a3', maxWidth: '700px', margin: '0 auto 1.5rem', lineHeight: '1.7' }}>
            Engineered for steel rolling mills and industrial units across Sahibabad, Loni, and Mohan Nagar. Eliminate weighbridge leakage, track billet yield ratios, and automate GST e-way billing effortlessly.
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap', fontWeight: '600', color: '#10b981' }}>
            <span><CheckCircle size={16} style={{ display: 'inline', marginRight: '6px' }} /> Weighbridge Automation</span>
            <span><CheckCircle size={16} style={{ display: 'inline', marginRight: '6px' }} /> Heat &amp; Batch Yield Tracking</span>
            <span><CheckCircle size={16} style={{ display: 'inline', marginRight: '6px' }} /> Tally ERP Sync</span>
          </div>
        </motion.div>

        {/* FAQs */}
        <FAQAccordion 
          title="Detailed Manufacturing ERP FAQs" 
          faqs={erpFaqsDetailed} 
          accentColor="#10b981" 
        />

        {/* Bottom High-Converting Contact Banner */}
        <div style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(6,182,212,0.15) 100%)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '24px', padding: '48px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#fff', marginBottom: '12px' }}>Transform Your Factory &amp; Plant Operations</h2>
          <p style={{ color: '#a3a3a3', maxWidth: '600px', margin: '0 auto 24px', fontSize: '1rem', lineHeight: '1.6' }}>
            Schedule an on-site plant audit in Delhi NCR or a virtual demonstration with our industrial ERP consultants.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={16} /> Schedule Plant Audit via WhatsApp
            </a>
            <a href="/contact" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <PhoneCall size={16} /> Request ERP Quote
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
